import { Redis } from "@upstash/redis";
import { REPORT_CACHE_VERSION_THROTTLE_SECONDS } from "@sajilni/config/constants";
import { getCacheEnv } from "./cache-key";

export {
  buildApiCacheKey,
  buildVersionedApiCacheKey,
  getCacheEnv,
  stableSerialize,
} from "./cache-key";
export {
  invalidateEventPageCache,
  type InvalidateEventPageCacheParams,
} from "./event-page-cache";

const DEFAULT_KEY_PREFIX = "sajilni";

export interface CacheClientOptions {
  url?: string;
  token?: string;
  keyPrefix?: string;
}

export interface CacheWriteOptions {
  ttlSeconds?: number;
}

export type ReportCacheScope = "financial" | "order" | "attendee";
export type ReportCacheDomainEventType =
  | "event.updated"
  | "ticket.changed"
  | "order.changed";

export interface ReportCacheDomainEvent {
  type: ReportCacheDomainEventType;
  eventId: string;
  /** When true on order.changed, also bumps the financial report scope */
  invalidateFinancial?: boolean;
}

export interface ResolveReportCacheScopesOptions {
  invalidateFinancial?: boolean;
}

const ALL_REPORT_CACHE_SCOPES: ReportCacheScope[] = [
  "financial",
  "order",
  "attendee",
];

export function resolveReportCacheScopes(
  type: ReportCacheDomainEventType,
  options: ResolveReportCacheScopesOptions = {}
): ReportCacheScope[] {
  switch (type) {
    case "order.changed": {
      const scopes: ReportCacheScope[] = ["order", "attendee"];
      if (options.invalidateFinancial) {
        scopes.push("financial");
      }
      return scopes;
    }
    case "ticket.changed":
      return ["financial", "order"];
    case "event.updated":
      return [...ALL_REPORT_CACHE_SCOPES];
    default:
      return [...ALL_REPORT_CACHE_SCOPES];
  }
}

export type CacheValue =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | unknown[];

let sharedClient: SajilniCache | null = null;

export class SajilniCache {
  private readonly redis: Redis;
  private readonly keyPrefix: string;

  constructor(options: CacheClientOptions = {}) {
    const url = options.url ?? process.env["UPSTASH_REDIS_REST_URL"];
    const token = options.token ?? process.env["UPSTASH_REDIS_REST_TOKEN"];

    if (!url || !token) {
      throw new Error(
        "Missing Redis configuration. Set UPSTASH_REDIS_REST_URL and UPSTASH_REDIS_REST_TOKEN (or pass url and token)."
      );
    }

    this.redis = new Redis({ url, token });
    // Product prefix stays "sajilni". Env isolation is the middle segment from
    // getCacheEnv() (SAJILNI_CACHE_KEY_PREFIX / SAJILNI_DEPLOYMENT_ENV / …).
    this.keyPrefix = normalizePrefix(options.keyPrefix ?? DEFAULT_KEY_PREFIX);
  }

  key(key: string): string {
    const normalizedKey = key.trim();
    if (!normalizedKey) {
      throw new Error("Cache key cannot be empty.");
    }

    // e.g. sajilni:staging:languages:all:v1
    return `${this.keyPrefix}:${getCacheEnv()}:${normalizedKey}`;
  }

  async read<T = unknown>(key: string): Promise<T | null> {
    return this.redis.get<T>(this.key(key));
  }

  async write<T extends CacheValue>(
    key: string,
    value: T,
    options: CacheWriteOptions = {}
  ): Promise<void> {
    const cacheKey = this.key(key);

    if (options.ttlSeconds !== undefined) {
      assertValidTtl(options.ttlSeconds);
      await this.redis.set(cacheKey, value, { ex: options.ttlSeconds });
      return;
    }

    await this.redis.set(cacheKey, value);
  }

  /**
   * Sets the key only if it does not already exist (Redis SET NX).
   * Returns true if the key was set, false if it already existed.
   */
  async setIfNotExists<T extends CacheValue>(
    key: string,
    value: T,
    options: CacheWriteOptions = {}
  ): Promise<boolean> {
    const cacheKey = this.key(key);

    if (options.ttlSeconds !== undefined) {
      assertValidTtl(options.ttlSeconds);
      const result = await this.redis.set(cacheKey, value, {
        nx: true,
        ex: options.ttlSeconds,
      });
      return result === "OK";
    }

    const result = await this.redis.set(cacheKey, value, { nx: true });
    return result === "OK";
  }

  async delete(key: string): Promise<number> {
    return this.redis.del(this.key(key));
  }

  /**
   * Atomically deletes a key only when its current value matches the
   * supplied token. Used to safely release a distributed lock without
   * accidentally freeing a lock that another worker has since taken.
   * Returns true when the key was deleted by this call.
   */
  async deleteIfValueMatches(
    key: string,
    expectedValue: string
  ): Promise<boolean> {
    const cacheKey = this.key(key);
    const script = `
      if redis.call('get', KEYS[1]) == ARGV[1] then
        return redis.call('del', KEYS[1])
      else
        return 0
      end
    `;
    const result = (await this.redis.eval(
      script,
      [cacheKey],
      [expectedValue]
    )) as number;
    return result === 1;
  }

  async has(key: string): Promise<boolean> {
    return (await this.redis.exists(this.key(key))) === 1;
  }

  async expire(key: string, ttlSeconds: number): Promise<boolean> {
    assertValidTtl(ttlSeconds);
    return (await this.redis.expire(this.key(key), ttlSeconds)) === 1;
  }

  async increment(key: string, by = 1): Promise<number> {
    if (!Number.isInteger(by) || by <= 0) {
      throw new Error("Increment value must be a positive integer.");
    }

    const cacheKey = this.key(key);
    const script = `return redis.call('incrby', KEYS[1], ARGV[1])`;
    const result = (await this.redis.eval(script, [cacheKey], [String(by)])) as
      | number
      | string;

    return typeof result === "string" ? Number(result) : result;
  }

  async getOrSet<T extends CacheValue>(
    key: string,
    resolver: () => Promise<T> | T,
    options: CacheWriteOptions = {}
  ): Promise<T> {
    const cachedValue = await this.read<T>(key);
    if (cachedValue !== null) return cachedValue;

    const value = await resolver();
    await this.write(key, value, options);
    return value;
  }
}

export function createCacheClient(
  options: CacheClientOptions = {}
): SajilniCache {
  return new SajilniCache(options);
}

export function getCacheClient(options: CacheClientOptions = {}): SajilniCache {
  if (options.url || options.token || options.keyPrefix) {
    return createCacheClient(options);
  }

  if (!sharedClient) {
    sharedClient = createCacheClient();
  }

  return sharedClient;
}

export async function readCache<T = unknown>(key: string): Promise<T | null> {
  return getCacheClient().read<T>(key);
}

export async function writeCache<T extends CacheValue>(
  key: string,
  value: T,
  options: CacheWriteOptions = {}
): Promise<void> {
  await getCacheClient().write(key, value, options);
}

export async function deleteCache(key: string): Promise<number> {
  return getCacheClient().delete(key);
}

export async function getOrSetCache<T extends CacheValue>(
  key: string,
  resolver: () => Promise<T> | T,
  options: CacheWriteOptions = {}
): Promise<T> {
  return getCacheClient().getOrSet(key, resolver, options);
}

function buildReportVersionKey(eventId: string, scope: ReportCacheScope): string {
  const normalizedEventId = eventId.trim();
  if (!normalizedEventId) {
    throw new Error("eventId is required to read or bump report cache versions.");
  }

  return `report-version:event:${normalizedEventId}:${scope}`;
}

function buildReportVersionThrottleKey(
  eventId: string,
  scope: ReportCacheScope
): string {
  return `report-version-throttle:event:${eventId.trim()}:${scope}`;
}

async function readReportCacheVersionValue(
  eventId: string,
  scope: ReportCacheScope
): Promise<string> {
  const value = await readCache<number>(buildReportVersionKey(eventId, scope));
  return String(value ?? 0);
}

export async function getReportCacheVersion(
  eventId: string,
  scope: ReportCacheScope
): Promise<string> {
  return readReportCacheVersionValue(eventId, scope);
}

export async function getReportCacheVersions(eventId: string): Promise<
  Record<ReportCacheScope, string>
> {
  const [financial, order, attendee] = await Promise.all(
    ALL_REPORT_CACHE_SCOPES.map((scope) =>
      readReportCacheVersionValue(eventId, scope)
    )
  );

  return { financial, order, attendee };
}

export async function bumpReportCacheVersions(
  eventId: string,
  scopes: ReportCacheScope[] = [...ALL_REPORT_CACHE_SCOPES]
): Promise<Record<ReportCacheScope, string>> {
  const uniqueScopes = Array.from(new Set(scopes));
  if (uniqueScopes.length === 0) {
    return getReportCacheVersions(eventId);
  }

  const cache = getCacheClient();
  const bumpedScopes = new Map<ReportCacheScope, string>();

  await Promise.all(
    uniqueScopes.map(async (scope) => {
      const claimed = await cache.setIfNotExists(
        buildReportVersionThrottleKey(eventId, scope),
        "1",
        { ttlSeconds: REPORT_CACHE_VERSION_THROTTLE_SECONDS }
      );

      if (!claimed) {
        bumpedScopes.set(
          scope,
          await readReportCacheVersionValue(eventId, scope)
        );
        return;
      }

      const value = await cache.increment(buildReportVersionKey(eventId, scope));
      bumpedScopes.set(scope, String(value));
    })
  );

  const untouchedScopes = ALL_REPORT_CACHE_SCOPES.filter(
    (scope) => !bumpedScopes.has(scope)
  );

  if (untouchedScopes.length === 0) {
    return {
      financial: bumpedScopes.get("financial")!,
      order: bumpedScopes.get("order")!,
      attendee: bumpedScopes.get("attendee")!,
    };
  }

  const untouchedVersions = await Promise.all(
    untouchedScopes.map(async (scope) => [
      scope,
      await readReportCacheVersionValue(eventId, scope),
    ] as const)
  );

  const nextVersions: Record<ReportCacheScope, string> = {
    financial: bumpedScopes.get("financial") ?? "0",
    order: bumpedScopes.get("order") ?? "0",
    attendee: bumpedScopes.get("attendee") ?? "0",
  };

  for (const [scope, value] of untouchedVersions) {
    nextVersions[scope] = value;
  }

  return nextVersions;
}

export async function emitReportCacheDomainEvent(
  event: ReportCacheDomainEvent
): Promise<Record<ReportCacheScope, string>> {
  const scopes = resolveReportCacheScopes(event.type, {
    invalidateFinancial: event.invalidateFinancial,
  });
  return bumpReportCacheVersions(event.eventId, scopes);
}

/**
 * Returns `{ hit: true }` if the cache returned data without calling the resolver,
 * and `{ hit: false }` if the resolver was invoked. Pass `hit` into the response
 * headers as `X-Cache: HIT` or `X-Cache: MISS`.
 *
 * Usage:
 *   const { data, hit } = await getOrSetCacheWithHit(key, resolver, opts);
 *   headers.set("X-Cache", hit ? "HIT" : "MISS");
 */
export async function getOrSetCacheWithHit<T extends CacheValue>(
  key: string,
  resolver: () => Promise<T> | T,
  options: CacheWriteOptions = {}
): Promise<{ data: T; hit: boolean }> {
  let client: SajilniCache;
  try {
    client = getCacheClient();
  } catch {
    return { data: await resolver(), hit: false };
  }

  try {
    const existing = await client.read<T>(key);
    if (existing !== null) return { data: existing, hit: true };
  } catch (err) {
    console.warn("[cache] read failed, falling back to resolver", err);
    return { data: await resolver(), hit: false };
  }

  const data = await resolver();
  client.write(key, data, options).catch((err) =>
    console.warn("[cache] write failed (non-blocking)", err)
  );
  return { data, hit: false };
}

function normalizePrefix(prefix: string | undefined): string {
  const normalizedPrefix = (prefix ?? DEFAULT_KEY_PREFIX)
    .trim()
    .replace(/^:+|:+$/g, "");

  return normalizedPrefix || DEFAULT_KEY_PREFIX;
}

function assertValidTtl(ttlSeconds: number): void {
  if (!Number.isInteger(ttlSeconds) || ttlSeconds <= 0) {
    throw new Error("Cache TTL must be a positive integer in seconds.");
  }
}
